import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError, Provider } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { syncUserToHubSpot, handleUserRegistration } from '../lib/hubspotIntegration';
import { claimEntitlements } from '../lib/claimEntitlements';

// OAuth provider configuration interface
interface OAuthConfig {
  google: boolean;
  github: boolean;
  microsoft: boolean;
}

// User role interface
interface UserRole {
  id: string;
  name: string;
  display_name: string;
  description: string;
  is_system_role: boolean;
}

// Permission interface
interface Permission {
  name: string;
  display_name: string;
  category: string;
  resource: string;
  action: string;
}

// Team membership interface
interface TeamMembership {
  team_id: string;
  team_name: string;
  role: string;
  permissions: Record<string, boolean>;
  is_active: boolean;
}

// Supabase response interfaces
interface UserRoleAssignmentResponse {
  user_roles: {
    id: string;
    name: string;
    display_name: string;
    description: string;
    is_system_role: boolean;
  };
}

interface TeamMemberResponse {
  team_id: string;
  role: string;
  is_active: boolean;
  teams: {
    name: string;
  } | null;
  team_roles: {
    permissions: Record<string, boolean>;
  } | null;
}

// Enhanced Auth Context interface with RBAC
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  rolesLoading: boolean;
  oauthConfig: OAuthConfig;
  userRoles: UserRole[];
  userPermissions: Permission[];
  teamMemberships: TeamMembership[];

  // Authentication methods
  signUp: (email: string, password: string, metadata?: object) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  signInWithOAuth: (provider: Provider) => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: { email?: string; password?: string; data?: object }) => Promise<{ error: AuthError | null }>;
  isOAuthAvailable: (provider: Provider) => boolean;

  // RBAC methods
  hasPermission: (permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
  hasTeamPermission: (teamId: string, permission: string) => boolean;
  refreshRoles: () => Promise<void>;

  // Role checking utilities
  isSystemAdmin: () => boolean;
  isBusinessOwner: () => boolean;
  isTeamManager: () => boolean;
  canManageUsers: () => boolean;
  canManageRoles: () => boolean;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use the Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component props
interface AuthProviderProps {
  children: React.ReactNode;
}

// Check OAuth configuration from environment variables
const getOAuthConfig = (): OAuthConfig => {
  return {
    google: !!import.meta.env.VITE_GOOGLE_CLIENT_ID,
    github: !!import.meta.env.VITE_GITHUB_CLIENT_ID,
    microsoft: !!import.meta.env.VITE_MICROSOFT_CLIENT_ID
  };
};

// AuthProvider component that wraps the app and provides auth state
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [oauthConfig] = useState<OAuthConfig>(getOAuthConfig());

  // RBAC state
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [teamMemberships, setTeamMemberships] = useState<TeamMembership[]>([]);

  // Fetch user roles and permissions
  const fetchUserRoles = async (userId: string) => {
    if (!userId) return;

    console.log('🔄 Starting fetchUserRoles for user:', userId);
    setRolesLoading(true);

    try {
      // First, ensure user exists in our users table
      console.log('👤 Checking/creating user in users table...');

      console.log('🔍 About to query users table...');

      // Add timeout wrapper to detect hanging queries
      const queryPromise = supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();

      console.log('⏰ Starting query with 10-second timeout...');

      let existingUser = null;
      let userCheckError = null;
      try {
        const result = await Promise.race([
          queryPromise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Query timeout after 10 seconds')), 10000)
          )
        ]);

        // Type guard to check if result is a Supabase response
        if (result && typeof result === 'object' && 'data' in result) {
          existingUser = (result as { data: unknown; error: unknown }).data;
          userCheckError = (result as { data: unknown; error: unknown }).error;
          console.log('✅ Query completed within timeout');
        }
      } catch (timeoutError) {
        console.error('⏰ Query timed out:', timeoutError);
        userCheckError = timeoutError;
      }

      console.log('📊 User check result:', { existingUser, userCheckError });

      if (userCheckError) {
        if (userCheckError.message === 'Query timeout after 10 seconds') {
          console.error('❌ Database query timed out - continuing with empty roles');
        } else if (userCheckError.code === 'PGRST116') {
          // User doesn't exist, create them
          console.log('👤 Creating user in users table...');
          console.log('📧 Using email:', user?.email);

          const { error: userCreateError } = await supabase
            .from('users')
            .insert({
              id: userId,
              email: user?.email || 'unknown@example.com',
              created_at: new Date().toISOString()
            });

          console.log('🏗️ User creation result:', { userCreateError });

          if (userCreateError) {
            console.error('❌ Error creating user:', userCreateError);
          } else {
            console.log('✅ User created successfully');
          }
        } else {
          console.error('❌ Unexpected user check error:', userCheckError);
        }
      } else {
        console.log('✅ User already exists');
      }

      // SKIP role assignments for now to focus on HubSpot integration
      console.log('⏩ Skipping role assignments to focus on HubSpot integration...');

      const rolesData = [];
      console.log('✅ Setting user roles:', rolesData);
      setUserRoles(rolesData);

      // For now, let's skip permissions and teams to get basic roles working
      // We can add these back once we confirm roles are working
      console.log('📋 Setting empty permissions and teams...');
      setUserPermissions([]);
      setTeamMemberships([]);

      console.log('🎉 fetchUserRoles completed successfully!');

    } catch (error) {
      console.error('❌ Exception in fetchUserRoles:', error);
      console.error('❌ Error stack:', error.stack);
      setUserRoles([]);
      setUserPermissions([]);
      setTeamMemberships([]);
    } finally {
      console.log('🏁 fetchUserRoles finally block - setting rolesLoading to false');
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    console.log('🚀 AuthContext useEffect starting...');
    setLoading(true);

    const initializeAuth = async () => {
      try {
        const {
          data: { session: initialSession },
          error: initialError
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (initialError) {
          console.error('❌ Error fetching initial auth session:', initialError);
        }

        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      } catch (error) {
        console.error('❌ Unexpected error getting initial auth session:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes (this works reliably)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 Auth state changed:', event, session?.user?.id || 'no user');
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Recovery must reach the password form, even if the email returns
        // through the generic callback or the configured site URL.
        if (event === 'PASSWORD_RECOVERY' && session?.user) {
          sessionStorage.setItem('og01_auth_next', '/auth/reset-password');
          if (window.location.pathname !== '/auth/reset-password') {
            setTimeout(() => window.location.replace('/auth/reset-password'), 0);
          }
          return;
        }

        // Clear roles when signing out
        if (!session?.user) {
          console.log('🚪 No user session, clearing roles');
          setUserRoles([]);
          setUserPermissions([]);
          setTeamMemberships([]);
          setRolesLoading(false);
        } else {
          // Fetch roles when signing in 
          // Do not await supabase calls in this callback — it holds the auth lock
          // and blocks every page query after login/refresh.
          setTimeout(() => {
            void (async () => {
              console.log('👤 User session found, about to fetch roles for:', session.user.id);

              try {
                console.log('🎯 Starting fetchUserRoles...');
                await fetchUserRoles(session.user.id);
                console.log('✅ fetchUserRoles completed successfully');
              } catch (rolesError) {
                console.error('❌ fetchUserRoles failed:', rolesError);
                // Continue execution even if roles fail
              }

              // /payment/success owns claiming, and only after complete-checkout returns paid: true.
              const onPaymentHandoff =
                typeof window !== "undefined" && window.location.pathname.startsWith("/payment/");
              if (!onPaymentHandoff) {
                try {
                  await claimEntitlements();
                } catch (claimError) {
                  console.warn('claimEntitlements failed (non-blocking):', claimError);
                }
              }

              // Handle HubSpot sync for user sessions (both new signups and existing users)
              // This ensures all users get synced to HubSpot regardless of when they joined
              console.log('🔄 Checking HubSpot sync environment...');

              // Skip automatic HubSpot sync in development mode due to CORS restrictions
              const isDevelopment = import.meta.env.MODE === 'development' ||
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1';

              if (isDevelopment) {
                console.log('🚫 Skipping automatic HubSpot sync in development mode (CORS restrictions)');
                console.log('💡 Use the HubSpot Test page at /hubspot-test to test integration');
                console.log('📧 User would be synced:', session.user.email);
                console.log('🆔 User ID:', session.user.id);
              } else {
                console.log('🔄 Triggering HubSpot user registration sync (PRODUCTION MODE)...');
                console.log('📧 User email:', session.user.email);
                console.log('🆔 User ID:', session.user.id);
                console.log('📊 User metadata:', session.user.user_metadata);

                try {
                  console.log('⚡ Calling server-side HubSpot sync API...');

                  // In production, call server-side API instead of direct HubSpot calls
                  const response = await fetch('/api/hubspot-sync', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      user: session.user,
                      action: 'sync'
                    })
                  });

                  const result = await response.json();

                  if (result.success) {
                    console.log('✅ Server-side HubSpot sync completed successfully:', result.hubspotId);
                  } else {
                    console.error('❌ Server-side HubSpot sync failed:', result.error);
                  }
                } catch (error) {
                  console.error('❌ HubSpot sync API call failed:', error);
                  // Don't block user authentication if HubSpot sync fails
                }
              }
            })();
          }, 0);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email: string, password: string, metadata?: object) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || {}
        }
      });
      return { error };
    } catch (error) {
      console.error('Error in signUp:', error);
      return { error: error as AuthError };
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      return { error };
    } catch (error) {
      console.error('Error in signIn:', error);
      return { error: error as AuthError };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      console.error('Error in signOut:', error);
      return { error: error as AuthError };
    }
  };

  // Email a one-time sign-in link. Does not create an account.
  const signInWithMagicLink = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${import.meta.env.VITE_SITE_URL || window.location.origin}/auth/callback`,
          shouldCreateUser: false,
        },
      });
      return { error };
    } catch (error) {
      console.error('Error in signInWithMagicLink:', error);
      return { error: error as AuthError };
    }
  };

  // Sign in with OAuth providers. Account OAuth is disabled.
  // Google Connect for Search Console / GA4 is unchanged.
  const signInWithOAuth = async (_provider: Provider) => {
    /*
    try {
      if (!isOAuthAvailable(provider)) {
        return {
          error: {
            message: `${provider} OAuth is not configured. Please check your environment variables.`,
            name: 'OAuthNotConfigured'
          } as AuthError
        };
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${import.meta.env.VITE_SITE_URL || window.location.origin}/auth/callback`
        }
      });
      return { error };
    } catch (error) {
      console.error('Error in signInWithOAuth:', error);
      return { error: error as AuthError };
    }
    */
    return {
      error: {
        message: 'OAuth sign-in is not available for OG01 accounts. Use email and password or a magic link.',
        name: 'OAuthDisabled',
      } as AuthError,
    };
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${import.meta.env.VITE_SITE_URL || window.location.origin}/auth/reset-password`
      });
      return { error };
    } catch (error) {
      console.error('Error in resetPassword:', error);
      return { error: error as AuthError };
    }
  };

  // Update user profile
  const updateProfile = async (updates: { email?: string; password?: string; data?: object }) => {
    try {
      const { error } = await supabase.auth.updateUser(updates);
      return { error };
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return { error: error as AuthError };
    }
  };

  // Check if OAuth provider is available
  const isOAuthAvailable = (provider: Provider): boolean => {
    switch (provider) {
      case 'google':
        // return oauthConfig.google;
        return false;
      case 'github':
        // return oauthConfig.github;
        return false;
      case 'azure':
        // return oauthConfig.microsoft;
        return false;
      default:
        return false;
    }
  };

  // RBAC Methods

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(p => p.name === permission);
  };

  // Check if user has a specific role
  const hasRole = (roleName: string): boolean => {
    return userRoles.some(role => role.name === roleName);
  };

  // Check if user has a specific team permission
  const hasTeamPermission = (teamId: string, permission: string): boolean => {
    const membership = teamMemberships.find(m => m.team_id === teamId && m.is_active);
    if (!membership) return false;

    // Check role-based permissions
    if (membership.role === 'owner') return true;
    if (membership.role === 'admin' && permission !== 'delete_team') return true;
    if (membership.role === 'manager' && ['read', 'create', 'manage_projects'].includes(permission)) return true;
    if (membership.role === 'member' && ['read', 'create', 'update_own'].includes(permission)) return true;
    if (membership.role === 'viewer' && permission === 'read') return true;

    // Check custom permissions
    return membership.permissions[permission] === true;
  };

  // Refresh user roles and permissions
  const refreshRoles = async (): Promise<void> => {
    if (user) {
      await fetchUserRoles(user.id);
    }
  };

  // Utility role checks
  const isSystemAdmin = (): boolean => hasRole('system_admin');
  const isBusinessOwner = (): boolean => hasRole('business_owner');
  const isTeamManager = (): boolean => hasRole('team_manager');
  const canManageUsers = (): boolean => hasPermission('system.users.manage');
  const canManageRoles = (): boolean => hasPermission('system.roles.manage');

  // Context value that will be provided to children
  const value: AuthContextType = {
    user,
    session,
    loading,
    rolesLoading,
    oauthConfig,
    userRoles,
    userPermissions,
    teamMemberships,
    signUp,
    signIn,
    signInWithMagicLink,
    signOut,
    signInWithOAuth,
    resetPassword,
    updateProfile,
    isOAuthAvailable,
    hasPermission,
    hasRole,
    hasTeamPermission,
    refreshRoles,
    isSystemAdmin,
    isBusinessOwner,
    isTeamManager,
    canManageUsers,
    canManageRoles
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 